import { Grid, Grow, Paper, Typography } from '@material-ui/core'
import { SvgIconProps } from '@material-ui/core/SvgIcon'
import useMediaQuery from '@material-ui/core/useMediaQuery'
import AccountBalanceWalletRoundedIcon from '@material-ui/icons/AccountBalanceWalletRounded'
import CheckCircleOutlineRoundedIcon from '@material-ui/icons/CheckCircleOutlineRounded'
import EmojiEventsRoundedIcon from '@material-ui/icons/EmojiEventsRounded'
import HourglassEmptyRoundedIcon from '@material-ui/icons/HourglassEmptyRounded'
import classNames from 'classnames'
import React, { useEffect, useState } from 'react'
import { styles } from './styles'
import { useCountUp } from './useCountUp'

interface OverpaymentData {
  amount: number
  remainingAmount: number
}

interface SummaryCardData {
  label: string
  value: number
  caption?: string
  highlight?: boolean
  Icon: React.ComponentType<SvgIconProps>
}

interface CashBackSummaryCardsProps {
  pending: number
  lastClosedOut: number
  receivableMilestone: number
  year: string | number
  totalPaidLabel: string
  overpayment?: OverpaymentData
}

const formatAmount = (value: number) => `$${Number(value || 0).toFixed(2)}`

const SummaryCard = ({
  card,
  index,
  mounted,
  reduceMotion,
  classes,
}: {
  card: SummaryCardData
  index: number
  mounted: boolean
  reduceMotion: boolean
  classes: ReturnType<typeof styles>
}) => {
  const animatedValue = useCountUp(card.value, 900, reduceMotion)
  const Icon = card.Icon

  return (
    <Grow
      in={mounted}
      timeout={reduceMotion ? 0 : 420}
      style={{
        transitionDelay: mounted && !reduceMotion ? `${index * 90}ms` : '0ms',
      }}
    >
      <Paper
        elevation={0}
        className={classNames(classes.card, {
          [classes.highlightCard]: card.highlight,
        })}
      >
        <div className={classes.cardHeader}>
          <span
            className={classNames(classes.iconBadge, {
              [classes.highlightIconBadge]: card.highlight,
            })}
          >
            <Icon fontSize="small" />
          </span>
          <Typography component="p" className={classes.cardLabel}>
            {card.label}
          </Typography>
        </div>
        <Typography component="p" className={classes.cardValue}>
          {formatAmount(animatedValue)}
        </Typography>
        {card.caption && (
          <Typography component="p" className={classes.cardCaption}>
            {card.caption}
          </Typography>
        )}
      </Paper>
    </Grow>
  )
}

const CashBackSummaryCards = ({
  pending,
  lastClosedOut,
  receivableMilestone,
  year,
  totalPaidLabel,
  overpayment,
}: CashBackSummaryCardsProps) => {
  const classes = styles()
  const reduceMotion = useMediaQuery('(prefers-reduced-motion: reduce)', {
    noSsr: true,
  })
  const [mounted, setMounted] = useState(false)
  const hasOverpayment = Number(overpayment?.remainingAmount) > 1

  useEffect(() => {
    setMounted(true)
  }, [])

  const cards: SummaryCardData[] = [
    {
      label: 'pending cash back',
      value: pending,
      caption: hasOverpayment
        ? 'applied to your overpayment balance below'
        : undefined,
      highlight: true,
      Icon: HourglassEmptyRoundedIcon,
    },
    {
      label: 'paid to you last month',
      value: lastClosedOut,
      Icon: CheckCircleOutlineRoundedIcon,
    },
    {
      label: `${totalPaidLabel}${year}`,
      value: receivableMilestone,
      Icon: EmojiEventsRoundedIcon,
    },
  ]

  if (overpayment && hasOverpayment) {
    cards.push({
      label: 'remaining overpayment balance',
      value: overpayment.remainingAmount,
      caption: `(${formatAmount(overpayment.amount)}) overpaid on 10/15/22*`,
      highlight: true,
      Icon: AccountBalanceWalletRoundedIcon,
    })
  }

  return (
    <Grid container spacing={2} className={classes.grid}>
      {cards.map((card, index) => (
        <Grid item xs={12} sm={6} md={4} key={card.label}>
          <SummaryCard
            card={card}
            index={index}
            mounted={mounted}
            reduceMotion={reduceMotion}
            classes={classes}
          />
        </Grid>
      ))}
    </Grid>
  )
}

export default CashBackSummaryCards
